import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import feedbackSurveyService from '@/services/feedbackSurveyService.ts';
import type { FeedbackSurveySource } from '@/types/feedbackSurvey.ts';
import { Button } from '@/components/ui/button';
import FeedbackSurveyModal from './FeedbackSurveyModal.tsx';

interface Props {
  source: FeedbackSurveySource;
}

// Banner non invasivo mostrato dopo il risultato di un percorso.
// Una volta per utente: resta visibile finché l'utente non invia il sondaggio
// (chiuderlo senza inviare lo ripropone alla prossima conclusione).
export default function FeedbackSurveyBanner({ source }: Props) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let active = true;
    feedbackSurveyService
      .getStatus()
      .then((status) => {
        if (active) setVisible(!status.alreadyCompleted);
      })
      .catch(() => {
        // In caso di errore non mostriamo il banner: meglio non disturbare.
        if (active) setVisible(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (!visible) return null;

  return (
    <>
      <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="flex items-center gap-4">
            {/* Accento verticale brand al posto di un'icona */}
            <span className="hidden h-12 w-1 shrink-0 rounded-full bg-gradient-to-b from-primary to-secondary sm:block" />
            <div>
              <p className="font-heading text-lg leading-tight text-foreground">
                {t('feedbackSurvey.bannerTitle')}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('feedbackSurvey.bannerSubtitle')}
              </p>
            </div>
          </div>
          <Button className="shrink-0 self-start sm:self-auto" onClick={() => setModalOpen(true)}>
            {t('feedbackSurvey.bannerCta')}
          </Button>
        </div>
      </div>

      <FeedbackSurveyModal
        isOpen={modalOpen}
        source={source}
        onClose={() => setModalOpen(false)}
        onCompleted={() => setVisible(false)}
      />
    </>
  );
}
